import React, { useState, useEffect, Suspense } from 'react';
import { IconSpinner } from '../../components';
import css from './AdminSocialWorkflowPage.css';

/**
 * StudioClientOnly — the SSR-safe boundary for the full-screen Workflow Studio.
 *
 * Mirrors ReviewerClientOnly: the repo's SSR is real (renderToString) and the
 * Studio uses framer-motion (reads the DOM) wrapping the reactflow editor (which
 * measures the DOM + ResizeObserver). We render a placeholder during SSR + the
 * first synchronous client render, then flip to the lazily-loaded Studio once
 * mounted. This also covers the REACT_APP_DEV_FAKE_ADMIN bypass (no server auth
 * redirect to keep the heavy tree off SSR). (WorkflowEditor's OWN ClientOnly adds
 * the reactflow vendor-CSS <link>; this guard sits above the motion chrome too.)
 */
const LazyStudio = React.lazy(() => import('./StudioInner'));

const Placeholder = () => (
    <div className={css.bootPlaceholder}>
        <IconSpinner />
    </div>
);

class StudioErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error('AdminSocialWorkflow Studio crashed:', error, info && info.componentStack);
    }
    render() {
        if (this.state.error) {
            return (
                <div className={css.bootPlaceholder} role="alert">
                    <p className={css.bootError}>Das Workflow-Studio konnte nicht geladen werden.</p>
                    <pre className={css.bootErrorDetail}>
                        {String(this.state.error && this.state.error.message)}
                    </pre>
                </div>
            );
        }
        return this.props.children;
    }
}

const StudioClientOnly = props => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return <Placeholder />;

    return (
        <StudioErrorBoundary>
            <Suspense fallback={<Placeholder />}>
                <LazyStudio {...props} />
            </Suspense>
        </StudioErrorBoundary>
    );
};

export default StudioClientOnly;
